import { appConfig } from '@common/config/app.config';
import { ConnectedSocket, removeClient, upsertClient } from '@common/reducers/ConnectedEnginesSlice';
import { getStore, safeDispatch } from '@common/state/state-provider';
import mqtt from 'mqtt';
import { getClientType, isExtensionContext, isWhatsappExtensionContext } from '../services/detect-context';
import { EngineCmdDedupCache } from './engine-cmd-gate';
import { isStateChangingEngineCommand, CHROME_SUPPORTED_COMMANDS, WIRE_CONTRACT_VERSION, type WaToolCommand } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';
import { buildEnginePresenceRecord, type EngineKind } from '@bt-common/notifier/presence';
import { EngineToolError, waToolError } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
import { getStableInstanceId, getExecutionContextTag } from './stable-instance-id';
import { computeReconnectBackoff } from './reconnect-backoff';
import { shouldResyncOnConnect, isWithinDebounce } from './reconnect-policy';
import { setConnectionStatus } from './NotifierReducer';
import { NotifierConnectionStatus } from './types';
import { parseWaPairing, type WaPairing } from './wa-pairing';
import { handleWhatsappInterfaceRequest } from "@common/tools/whatsapp";
import { logTabWakeEvent } from "@common/services/tab-wake-logger";
import { createExtensionBackendActionsClient } from '@extension/wa-communication-layer/api/backend-actions-client.adapter';
import type { BackendActionsClient } from '@bt-common/scheduling/send-protocol.types';

function logMqttSubscriberEvent(eventType: string, extra: Record<string, any> = {}) {
  logTabWakeEvent({ eventType, source: 'wa-tab-mqtt-subscriber', ...extra });
}

/**
 * MQTT Client Events Implementation:
 * 
 * CHANGED: Replaced system topic subscriptions with webhook-based client tracking:
 * - OLD: Subscribed to '$SYS/brokers/+/clients/+/connected' and '$SYS/brokers/+/clients/+/disconnected'
 * - NEW: EMQX webhooks publish client events to 'users/{userId}/downstream/client-events'
 * 
 * Backend Implementation:
 * - EMQX configured with webhooks that call backend '/api/v1/emqx/webhook'
 * - Backend tracks clients in Redis sets with 7-day TTL: {user:userId}:clients (set) + {user:userId}:client:{clientId}:data (metadata)
 * - Backend publishes complete clients list on connect/disconnect events only
 * 
 * Message format: {
 *   event: 'connected' | 'disconnected',
 *   clientId: string,
 *   timestamp: number,
 *   connectedClients: ConnectedSocket[] // Complete current clients list
 * }
 * 
 * Benefits:
 * - No processing of ALL broker client events (scalable)
 * - Automatic Redis TTL cleanup for stale connections (6-hour expiry)
 * - Complete state updates on each change (no sync issues)
 * - Backward compatible API (returns empty array)
 * - Simple connect/disconnect tracking only
 */

export interface MQTTConfig {
  brokerUrl: string;
  username: string;
  password: string;
  userId?: string;
}
/** Connection lifecycle states. At most one client exists; `connectGeneration` invalidates stale async work. */
type ConnState = 'disconnected' | 'connecting' | 'connected';

export class MQTTSubscriber {
  private static instance: MQTTSubscriber;
  private client: mqtt.MqttClient | null = null;
  private config: MQTTConfig | null = null;
  private userId: string | null = null;
  private currentClientId: string | null = null;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private subscribedTopics: Set<string> = new Set();
  private isConnecting: boolean = false;
  private connState: ConnState = 'disconnected';
  private connectGeneration = 0;
  private hasEverConnected = false; // true once a CONNACK arrived for the CURRENT client
  private debounceTimer: NodeJS.Timeout | null = null;
  private presenceHeartbeatTimer: NodeJS.Timeout | null = null;
  private staleEngineTimer: NodeJS.Timeout | null = null;
  private connectionHealthTimer: NodeJS.Timeout | null = null;
  private lastSuccessfulPublish: number = 0;
  private beforeUnloadHandler: (() => void) | null = null;
  private isDebug: boolean = false;
  private lastForcedPingAt: number = 0;
  private isDisconnectingManually: boolean = false; // Track manual disconnect to prevent publish race
  private backendActionsClient: BackendActionsClient | null = null;
  // #236 (3): client-side dedup of repeat MQTT deliveries of the same _engineCmdId.
  private readonly engineCmdDedup = new EngineCmdDedupCache();

  // Extra fields merged into the retained presence payload (e.g. the
  // team-inbox tab sets { teamInboxOpen: true } while its view is mounted).
  private presenceExtras: { teamInboxOpen?: boolean; waPairing?: WaPairing } = {};

  // Exponential-backoff reconnect state. mqtt.js retries on a FIXED reconnectPeriod; under a
  // broker/LB that accepts the WS but never CONNACKs, a fixed 5s retry opens sockets faster than
  // the connectTimeout reaps them → ESTABLISHED sockets pile up to Chrome's per-renderer cap →
  // ERR_INSUFFICIENT_RESOURCES. We grow the period per consecutive failed attempt (reset on CONNACK).
  private reconnectAttempt = 0;
  // Ghost GC: MQTT5 session-expiry (seconds). An abandoned session (reload → fresh clientId, old one
  // never reconnects) is reaped this long after its socket drops — vs the broker default 2h. A genuine
  // reconnect within the window resumes and gets its queued calendar/status deltas (cross-blip delivery).
  // Graceful reload reaps to 0 immediately via the beforeunload DISCONNECT. Browser surfaces only —
  // gateway pods keep MQTT 3.1.1 + clean:false (stable sessionId, real offline need).
  private static readonly SESSION_EXPIRY_S = 120;
  private sessionReaped = false; // guards the beforeunload+pagehide double-fire of the graceful reap
  private connackCount = 0;      // successful CONNACKs this realm has seen — 1 = first connect
  private lastResyncAt = 0;      // epoch ms of the last gap-resync, to debounce reconnect flaps
  private static readonly RESYNC_DEBOUNCE_MS = 15000;
  private static readonly RECONNECT_BASE_MS = 5000;
  private static readonly RECONNECT_CAP_MS = 60000;
  private static readonly RECONNECT_FACTOR = 2;
  private static readonly RECONNECT_JITTER = 0.3;

  private constructor() { }

  /**
   * Check if the underlying WebSocket is dead (CLOSING or CLOSED state).
   * After a debugger breakpoint or long suspension, the WebSocket can die
   * while mqtt.js client flags remain stale (connected=true, reconnecting=true).
   */
  private isWebSocketDead(): boolean {
    if (!this.client) {
      console.warn('[MQTT.diag] isWebSocketDead: no client');
      return true;
    }

    try {
      const stream = (this.client as any).stream;
      if (!stream) {
        console.warn('[MQTT.diag] isWebSocketDead: no stream (cannot determine, assuming alive)');
        return false;
      }

      // Check WebSocket readyState directly (if the stream wraps a WebSocket)
      const ws = stream.socket || stream;
      if (ws && typeof ws.readyState === 'number') {
        // WebSocket.CLOSING = 2, WebSocket.CLOSED = 3
        if (ws.readyState >= 2) {
          console.warn('[MQTT.diag] isWebSocketDead: WebSocket readyState =', ws.readyState, '(CLOSING/CLOSED)');
          return true;
        }
      }

      // Also check if the stream is not writable
      if (typeof stream.writable === 'boolean' && !stream.writable) {
        console.warn('[MQTT.diag] isWebSocketDead: stream.writable=false');
        return true;
      }
    } catch (e) {
      console.warn('[MQTT.diag] isWebSocketDead: check threw', (e as Error)?.message);
    }

    return false;
  }

  /**
   * Check if the MQTT client is in a state suitable for publishing.
   * This prevents race conditions where the WebSocket is transitioning states.
   */
  /**
   * Probe the outbound MQTT write path. Publishes a no-op message with QoS 1
   * and resolves when the broker ACKs the publish (callback fires), rejects on
   * error. If Chrome has deferred the WebSocket write for a hidden tab, the
   * callback never fires — the ping-handler probe times out and we wake.
   *
   * No subscriber is needed on the probe topic; QoS 1 ACK comes from the broker.
   */
  public probeMqttWrite(): Promise<void> {
    // Same BT_MQTT_DIAG gate as the healthCheck tick — happy-path probe
    // logs stay silent; real failures (not-ready, callback error, sync
    // throw) still log because they're the actionable signal.
    const diag = typeof localStorage !== 'undefined' && localStorage.getItem('BT_MQTT_DIAG') === '1';
    return new Promise<void>((resolve, reject) => {
      if (diag) {
        console.warn('[MQTT.diag] probeMqttWrite: start', {
          hasClient: !!this.client,
          connected: this.client?.connected,
          reconnecting: this.client?.reconnecting,
          disconnecting: this.client?.disconnecting,
          isDisconnectingManually: this.isDisconnectingManually,
          userId: this.userId,
        });
      }
      if (!this.isClientReadyForPublish() || !this.userId) {
        console.warn('[MQTT.diag] probeMqttWrite: rejecting mqtt-not-ready (client not ready or no userId)');
        reject(new Error('mqtt-not-ready'));
        return;
      }
      const topic = `users/${this.userId}/upstream/probes/${Date.now()}`;
      const t0 = Date.now();
      try {
        this.client!.publish(topic, 'probe', { qos: 1 }, (error) => {
          const dt = Date.now() - t0;
          if (error) {
            console.warn('[MQTT.diag] probeMqttWrite: publish callback error after', dt, 'ms:', error.message);
            reject(error);
          } else {
            if (diag) console.warn('[MQTT.diag] probeMqttWrite: publish ACKed after', dt, 'ms');
            resolve();
          }
        });
      } catch (error) {
        console.warn('[MQTT.diag] probeMqttWrite: publish threw synchronously:', (error as Error)?.message);
        reject(error as Error);
      }
    });
  }

  /**
   * True when this subscriber has a Blueticks userId attached — i.e. the
   * user has signed into Blueticks. Callers can gate MQTT probes on this
   * to avoid no-op rejects while the user is logged out. Note: this
   * tracks the *Blueticks* identity, not the WhatsApp web login state
   * (we don't observe WAWeb readiness here, by design).
   */
  public hasIdentity(): boolean {
    return !!this.userId;
  }

  private isClientReadyForPublish(): boolean {
    if (!this.client) {
      console.warn('[MQTT.diag] isClientReadyForPublish: no client');
      return false;
    }
    if (!this.client.connected) {
      console.warn('[MQTT.diag] isClientReadyForPublish: client.connected=false', {
        reconnecting: this.client.reconnecting,
        disconnecting: this.client.disconnecting,
      });
      return false;
    }
    if (this.client.disconnecting) {
      console.warn('[MQTT.diag] isClientReadyForPublish: client.disconnecting=true');
      return false;
    }
    if (this.client.reconnecting) {
      console.warn('[MQTT.diag] isClientReadyForPublish: client.reconnecting=true');
      return false;
    }
    if (this.isDisconnectingManually) {
      console.warn('[MQTT.diag] isClientReadyForPublish: isDisconnectingManually=true');
      return false;
    }

    // Additional check: verify the underlying stream is writable
    // This catches cases where connected=true but WebSocket is already closing
    try {
      const stream = (this.client as any).stream;
      if (stream && typeof stream.writable === 'boolean' && !stream.writable) {
        console.warn('[MQTT.diag] isClientReadyForPublish: stream.writable=false');
        return false;
      }
    } catch (e) {
      console.warn('[MQTT.diag] isClientReadyForPublish: stream check threw', (e as Error)?.message);
    }

    return true;
  }

  public static getInstance(): MQTTSubscriber {
    if (!MQTTSubscriber.instance) {
      MQTTSubscriber.instance = new MQTTSubscriber();
    }
    return MQTTSubscriber.instance;
  }


  public setDebug(isDebug: boolean): void {
    this.isDebug = isDebug;
  }

  /**
   * Merge extra fields into this client's presence payload and republish
   * immediately so other clients (the WhatsApp tab's event gate) learn the
   * change within ~1s instead of waiting for the 30s heartbeat.
   */
  public setPresenceExtras(extras: { teamInboxOpen?: boolean; waPairing?: WaPairing }): void {
    this.presenceExtras = { ...this.presenceExtras, ...extras };
    const canPublishNow = this.isClientReadyForPublish();
    // Rare event (view mount/unmount) — log unconditionally so presence-gating
    // issues are diagnosable from a plain console without enabling debug mode.
    console.log('[MQTT] presence extras updated', extras, canPublishNow ? '→ republished now' : '→ deferred until connect (will ride the next presence publish)');
    if (canPublishNow) {
      this.publishPresenceOnline();
    }
  }

  private log(...args: any[]): void {
    if (this.isDebug) {
      console.log(...args);
    }
  }

  /** Advance the connect generation and return the new token. Any async work captures the token
   *  it was started under and must no-op if a newer generation has since superseded it. */
  private nextGeneration(): number {
    this.connectGeneration += 1;
    return this.connectGeneration;
  }

  /** The ONLY pending-reconnect timer (single reconnect manager — mqtt.js auto-reconnect is off). */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  /** Schedule ONE reconnect attempt with exponential backoff. Deduped: a pending attempt is never
   *  stacked. Generation-guarded: superseded by any newer connect()/forceReconnect(). */
  private scheduleReconnect(reason: string): void {
    if (this.reconnectTimer) return;               // one pending attempt at a time
    if (this.isDisconnectingManually) return;      // deliberate teardown owns its own lifecycle
    this.reconnectAttempt++;
    const delay = computeReconnectBackoff(this.reconnectAttempt, {
      baseMs: MQTTSubscriber.RECONNECT_BASE_MS,
      capMs: MQTTSubscriber.RECONNECT_CAP_MS,
      factor: MQTTSubscriber.RECONNECT_FACTOR,
      jitter: MQTTSubscriber.RECONNECT_JITTER,
    });
    console.warn(`[MQTT.diag] scheduleReconnect(${reason}) attempt=${this.reconnectAttempt} in ${delay}ms`);
    const myGeneration = this.connectGeneration;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (myGeneration !== this.connectGeneration) return; // a newer connect/forceReconnect owns recovery
      this.forceReconnect();
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Update the connection status in Redux
   * @param status - 'CONNECTED' | 'AUTHENTICATED' | 'DISCONNECTED'
   */
  private updateConnectionStatus(status: 'CONNECTED' | 'AUTHENTICATED' | 'DISCONNECTED'): void {
    try {
      const statusMap = {
        'CONNECTED': NotifierConnectionStatus.CONNECTED,
        'AUTHENTICATED': NotifierConnectionStatus.AUTHENTICATED,
        'DISCONNECTED': NotifierConnectionStatus.DISCONNECTED
      };
      safeDispatch(setConnectionStatus(statusMap[status]));
      this.log(`[MQTT] Connection status updated to: ${status}`);
    } catch (error) {
      console.error('[MQTT] Failed to update connection status:', error);
    }
  }

  public getCurrentClientId(): string | null {
    return this.currentClientId;
  }

  public getCurrentClientType(): ReturnType<typeof getClientType> | null {
    return this.currentClientId ? getClientType() : null;
  }

  public connect(userId: string, accessToken: string): void {
    // Single-flight: never start a second connect while one is connecting or already connected
    // on a live socket. This is what prevents two clients with the SAME stable clientId from
    // existing at once (which EMQX resolves with a takeover -> infinite war).
    if (this.connState === 'connecting') {
      console.warn('[MQTT] connect: already connecting — ignoring duplicate');
      return;
    }
    if (this.connState === 'connected' && this.client && !this.isWebSocketDead()) {
      console.warn('[MQTT] connect: already connected on a live socket — ignoring duplicate');
      return;
    }

    if (typeof localStorage !== 'undefined' && localStorage.getItem('BT_MQTT_DIAG') === '1') {
      console.warn('[MQTT.diag] connect: called', {
        hasClient: !!this.client,
        connected: this.client?.connected,
        reconnecting: this.client?.reconnecting,
        disconnecting: this.client?.disconnecting,
        isConnecting: this.isConnecting,
        isDisconnectingManually: this.isDisconnectingManually,
      });
    }
    // Check client's actual state - more robust than manual flags
    if (this.client && (this.client.connected || this.client.reconnecting || this.client.disconnecting)) {
      // Verify the underlying WebSocket is actually alive
      // After a debugger breakpoint or long pause, the WebSocket can die while
      // client flags (connected/reconnecting) remain stale
      const isWebSocketDead = this.isWebSocketDead();
      if (isWebSocketDead) {
        console.warn('[MQTT] Client flags say connected/reconnecting but WebSocket is dead — forcing cleanup');
        this.cleanupClient();
        this.client = null;
        this.currentClientId = null;
        // Fall through to create a new connection
      } else {
        console.warn('[MQTT.diag] connect: early-exit — already connected/reconnecting/disconnecting, skipping', {
          connected: this.client.connected,
          reconnecting: this.client.reconnecting,
          disconnecting: this.client.disconnecting
        });
        return;
      }
    }

    // Check if already attempting to connect
    if (this.isConnecting) {
      console.warn('[MQTT.diag] connect: early-exit — isConnecting=true, skipping duplicate connection');
      return;
    }

    // Clean up any existing client and its listeners
    if (this.client) {
      this.log('[MQTT] Cleaning up existing client before creating new connection');
      this.cleanupClient();
    }

    this.log('[MQTT] Connecting to broker...');
    this.isConnecting = true;
    this.connState = 'connecting';
    void this.nextGeneration();
    this.isDisconnectingManually = false; // Reset disconnect flag for new connection

    const mqttBrokerUrl = appConfig.MQTT_BROKER_URL;
    this.log('[MQTT] Connecting to broker...', mqttBrokerUrl)
    this.userId = userId;
    this.config = {
      brokerUrl: appConfig.MQTT_BROKER_URL || "wss://mqtt.blueticks.co:443/mqtt",
      username: userId, // userId as username
      password: accessToken, // JWT token as password
      userId
    };

    const clientType = getClientType();

    // Stable per-(surface,execution-context) clientId. The execution-context tag keeps the
    // page main-world and a content script in the SAME tab distinct even though they share
    // sessionStorage — otherwise they'd resolve identical ids and take each other's MQTT
    // session over (clean 1000 takeover). Stable across reconnects so a reconnect resumes its
    // own session instead of leaving a ghost (clean:false).
    const generatedClientId =
      `${clientType.toLowerCase()}_${userId}_${getExecutionContextTag()}_${getStableInstanceId(clientType)}`;
    this.currentClientId = generatedClientId;

    try {
      // Create presence topic for LWT
      const presenceTopic = `users/${userId}/presence/${generatedClientId}`;

      this.client = mqtt.connect(this.config.brokerUrl, {
        clientId: this.currentClientId,
        username: this.config.username,
        password: this.config.password,
        clean: false,
        // SINGLE RECONNECT MANAGER: mqtt.js's internal auto-reconnect is DISABLED. All recovery goes
        // through scheduleReconnect() → forceReconnect() (generation-guarded, exponential backoff).
        // Rationale (2026-07-05 takeover-war forensics): with an internal reconnect loop, a client
        // object orphaned during teardown (end() racing an in-flight reconnect, or the old
        // applyReconnectBackoff() timer re-arm surviving end()) kept auto-reconnecting FOREVER with
        // its listeners removed — a silent ghost fighting the live client for the same stable
        // clientId (EMQX ReasonCode=142 ping-pong every ~1s, seen fleet-wide). With
        // reconnectPeriod: 0 an orphaned client object physically cannot retry.
        reconnectPeriod: 0,
        // A WS that opens to the LB but never gets a CONNACK squats an ESTABLISHED socket for the
        // whole connectTimeout. 60s × a 5s retry across many tabs = socket pileup → Insufficient
        // resources. 15s reaps abandoned half-open sockets 4× faster; backoff (below) thins retries.
        connectTimeout: 15000,
        keepalive: 60,
        rejectUnauthorized: false,
        protocol: 'wss',
        // Add additional stability options
        reschedulePings: true,
        protocolVersion: 5, // MQTT 5.0 — needed for per-session sessionExpiryInterval (ghost GC)
        // Chrome throttles native timers in background tabs (max 1/minute after 5min).
        // We bypass this via setupAlarmKeepalive() which piggybacks on the background
        // script's chrome.alarms (fires every 5s, we ping every 20s). chrome.alarms and
        // chrome.tabs.sendMessage are NOT throttled by Chrome's background tab policy.
        //
        // If issues persist, additional options:
        // 1. timerVariant: 'worker' — Web Worker timers are NOT throttled by Chrome.
        // 2. Reduce keepalive from 60 to 15 — smaller window for missed pings.
        // 3. navigator.locks.request('mqtt-keepalive', () => new Promise(() => {}))
        // 4. Move MQTT to an offscreen document (manifest has "offscreen" permission).
        manualConnect: false,
        // WA Web's page CSP (`worker-src` allow-list) blocks blob: workers,
        // so mqtt.js's 'worker' timerVariant always throws inside
        // `_setupKeepaliveManager` and falls back to native setInterval —
        // logging a noisy CSP violation each time. The actual background-
        // tab keepalive that prevents disconnects is `setupAlarmKeepalive()`
        // via chrome.alarms (lines 348-350 below); the 'worker' setting
        // here was dead code in WA's tab. Use 'native' to keep the page
        // console quiet; chrome.alarms remains the real anti-throttle path.
        timerVariant: 'native',
        properties: {
          // Increase packet size to 10MB to handle large responses (e.g., all contacts)
          maximumPacketSize: 10 * 1024 * 1024,  // 10MB
          // MQTT5 ghost GC: cap how long an abandoned session survives after its socket drops.
          // cleanStart=false (clean:false above) still resumes within this window for cross-blip delivery.
          sessionExpiryInterval: MQTTSubscriber.SESSION_EXPIRY_S,
        },
        // Add LWT for presence clearing
        // Note: retain is set to true to clear the retained presence message
        // The empty payload with retain=true removes the previous retained message
        will: {
          topic: presenceTopic,
          payload: '',
          qos: 1,
          retain: true,  // This clears the retained message
          properties: { messageExpiryInterval: 1 }  // Expire immediately so subscribers don't see stale offline messages
        }
      });

      this.setupEventHandlers();
    } catch (error) {
      console.error('[MQTT] Connection error:', error);
      // Reset client and connection state on error
      this.client = null;
      this.currentClientId = null;
      this.isConnecting = false;
    }
  }

  public disconnect(): void {
    // Set flag immediately to prevent any async operations from publishing
    this.isDisconnectingManually = true;
    this.clearReconnectTimer();

    if (this.client) {
      this.log('[MQTT] Disconnecting client:', this.currentClientId);

      // Stop presence heartbeat first to prevent more publishes
      this.stopPresenceHeartbeat();

      // Try to publish presence offline (best-effort, LWT is backup)
      this.publishPresenceOffline();

      this.cleanupClient();
    }

    this.client = null;
    this.currentClientId = null;
    this.isConnecting = false;
    this.isDisconnectingManually = false; // Reset for next connection
    this.connState = 'disconnected';
  }

  private cleanupClient(onEnded?: () => void): void {
    if (!this.client) { if (onEnded) onEnded(); return; }

    this.log('[MQTT] Cleaning up client listeners and connection');

    // Remove our listeners FIRST so the end()-driven close/offline don't re-enter our handlers.
    this.client.removeAllListeners();

    // Belt-and-suspenders vs ghost clients: even though reconnectPeriod is 0, zero it on the live
    // object and clear any internal reconnect timer BEFORE end(), so a leaked reference can never
    // retry (the 2026-07-05 takeover war was exactly such an orphan fighting its replacement).
    try {
      const raw = this.client as unknown as { options?: { reconnectPeriod?: number }; _clearReconnect?: () => void };
      if (raw.options) raw.options.reconnectPeriod = 0;
      raw._clearReconnect?.();
    } catch { /* best-effort */ }

    // Force-close; fire onEnded once the client has fully ended so a caller can safely reconnect
    // the same clientId without overlapping the old connection.
    try {
      this.client.end(true, undefined, onEnded);
    } catch {
      if (onEnded) onEnded();
    }

    // Clear subscription tracking
    this.subscribedTopics.clear();

    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Stop presence heartbeat
    this.stopPresenceHeartbeat();

    // Stop connection health check
    this.stopConnectionHealthCheck();

    // Remove beforeunload handler
    this.unregisterBeforeUnloadHandler();

    // Stop stale engine sweep
    this.stopStaleEngineSweep();

    this.isConnecting = false; // Reset connection flag
    this.connState = 'disconnected';
  }

  public onMessage(eventType: string, handler: (data: any) => void): void {
    this.messageHandlers.set(eventType, handler);
  }

  /**
   * Publish presence online status
   * Includes safeguards against WebSocket state race conditions
   */
  private publishPresenceOnline(): void {
    // Use comprehensive check instead of just this.client.connected
    if (!this.isClientReadyForPublish() || !this.userId || !this.currentClientId) {
      this.log('[MQTT] Skipping presence online publish - client not ready');
      return;
    }

    const presenceTopic = `users/${this.userId}/presence/${this.currentClientId}`;
    const clientType = getClientType();

    // Engine version (manifest; extension + Chrome gateway). The backend's claude-agents
    // rollout gate reads it; the web app has no manifest → legacy default '1.0.0'.
    const appVersion = globalThis.chrome?.runtime?.getManifest?.()?.version ?? '1.0.0';
    const { teamInboxOpen, waPairing } = this.presenceExtras;
    // The extension and the Chrome gateway are engines: their record is the
    // bt-common EnginePresence (the one builder). The web app is not an engine:
    // its own small record, no engineKind / commands.
    const engineKind: EngineKind | undefined =
      clientType === 'gateway' ? 'chrome-gateway' : clientType === 'whatsapp' ? 'extension' : undefined;
    const engineRecord = engineKind
      ? buildEnginePresenceRecord({
          clientId: this.currentClientId,
          // Rollout window only (older backends read it). @7.12 delete.
          browserType: engineKind === 'chrome-gateway' ? 'Gateway' : 'WhatsApp Web',
          engineKind,
          commands: Object.keys(CHROME_SUPPORTED_COMMANDS) as WaToolCommand[],
          contractVersion: WIRE_CONTRACT_VERSION,
          appVersion,
          teamInboxOpen,
        }, Date.now())
      : undefined;
    // Extension only: whether its WhatsApp Web is linked (see wa-pairing.ts). Rides beside the
    // bt-common record rather than in it, so older readers just ignore the extra key.
    const record = engineRecord
      ? { ...engineRecord, ...(engineKind === 'extension' && waPairing ? { waPairing } : {}) }
      : {
          clientId: this.currentClientId,
          device: 'Desktop',
          browserType: clientType === 'frontend' ? 'Website' : 'Unknown',
          app: appVersion,
          ts: Math.floor(Date.now() / 1000),
          ...(teamInboxOpen !== undefined ? { teamInboxOpen } : {}),
        };
    const presencePayload = JSON.stringify(record);

    try {
      // Double-check state right before publish (minimize race window)
      if (!this.isClientReadyForPublish()) {
        this.log('[MQTT] Skipping presence online publish - client state changed');
        return;
      }

      this.client!.publish(presenceTopic, presencePayload, {
        qos: 1,
        retain: true,
        properties: { messageExpiryInterval: 240 }
      }, (error) => {
        if (error) {
          // Don't log as error if we're disconnecting - this is expected
          if (this.isDisconnectingManually || this.client?.disconnecting) {
            this.log(`[MQTT] Presence online publish skipped during disconnect`);
          } else {
            console.warn(`[MQTT] Failed to publish presence online (will retry on next heartbeat):`, error.message);
          }
        } else {
          this.log(`[MQTT] Published presence online to ${presenceTopic}`);
          // Track successful publish for health monitoring
          this.lastSuccessfulPublish = Date.now();
        }
      });
    } catch (error) {
      // Catch synchronous errors (e.g., WebSocket already closed)
      this.log(`[MQTT] Caught error during presence online publish:`, error);
    }
  }

  /**
   * Publish presence offline status
   * Note: This is best-effort during disconnect - if it fails, the LWT will handle it
   */
  private publishPresenceOffline(): void {
    // For offline, we're more lenient - try even if disconnecting since LWT is backup
    if (!this.client || !this.userId || !this.currentClientId) return;

    // Still check if WebSocket is usable (not already closed)
    if (!this.client.connected && !this.client.reconnecting) {
      this.log('[MQTT] Skipping presence offline publish - already disconnected (LWT will handle it)');
      return;
    }

    const presenceTopic = `users/${this.userId}/presence/${this.currentClientId}`;

    try {
      this.client.publish(presenceTopic, '', {
        qos: 1,
        retain: true,
        properties: { messageExpiryInterval: 1 }  // Expire immediately, consistent with LWT
      }, (error) => {
        if (error) {
          // This is expected during disconnect - LWT will handle presence clearing
          this.log(`[MQTT] Presence offline publish failed (LWT will handle it):`, error.message);
        } else {
          this.log(`[MQTT] Published presence offline to ${presenceTopic}`);
        }
      });
    } catch (error) {
      // Catch synchronous errors - LWT will handle presence clearing
      this.log(`[MQTT] Caught error during presence offline publish (LWT will handle it):`, error);
    }
  }

  /**
   * Start presence heartbeat to keep presence fresh and prevent LWT race conditions
   */
  private startPresenceHeartbeat(): void {
    // Clear any existing heartbeat
    this.stopPresenceHeartbeat();

    // Publish presence every 30 seconds to keep it fresh
    // This prevents LWT empty messages from making clients appear offline
    this.presenceHeartbeatTimer = setInterval(() => {
      // Use comprehensive check to avoid publishing during state transitions
      if (this.isClientReadyForPublish()) {
        this.log('[MQTT] Publishing presence heartbeat');
        this.publishPresenceOnline();
      } else {
        this.log('[MQTT] Skipping heartbeat - client not ready for publish');
      }
    }, 30000); // 30 seconds

    this.log('[MQTT] Started presence heartbeat (30s interval)');
  }

  /**
   * Stop presence heartbeat
   */
  private stopPresenceHeartbeat(): void {
    if (this.presenceHeartbeatTimer) {
      clearInterval(this.presenceHeartbeatTimer);
      this.presenceHeartbeatTimer = null;
      this.log('[MQTT] Stopped presence heartbeat');
    }
  }

  /**
   * Listen for chrome.alarms ALARM_TRIGGERED messages from the background script
   * to force MQTT keepalive pings. chrome.alarms + chrome.tabs.sendMessage are NOT
   * throttled by Chrome's background tab policy, so this ensures the MQTT connection
   * stays alive even when the WhatsApp tab is in the background.
   *
   * The background script fires the alarm every 5s. We throttle pings to every 20s.
   */
  private setupAlarmKeepalive(): void {
    if (typeof chrome === 'undefined' || !chrome?.runtime?.onMessage) return;

    // Avoid registering multiple listeners
    if ((this as any)._alarmKeepaliveRegistered) return;
    (this as any)._alarmKeepaliveRegistered = true;

    chrome.runtime.onMessage.addListener((message, _sender, sendResponse) => {
      if (message.type !== 'ALARM_TRIGGERED') return;

      // Throttle: only ping every 20 seconds
      const now = Date.now();
      if (now - this.lastForcedPingAt < 20000) return;

      // Use comprehensive check to avoid pinging during state transitions
      if (this.isClientReadyForPublish()) {
        this.lastForcedPingAt = now;
        try {
          (this.client as any).sendPing();
        } catch (e) {
          // Ignore — sendPing may not exist in all mqtt.js versions or connection may have closed
        }
      }
    });

    this.log('[MQTT] Alarm-based keepalive registered (piggybacks on background script alarm every 20s)');
  }

  /**
   * Register beforeunload handler so closing the tab/browser publishes offline
   * presence before the page is destroyed. This covers cases where disconnect()
   * is never called (e.g. user closes tab).
   */
  private registerBeforeUnloadHandler(): void {
    this.unregisterBeforeUnloadHandler();

    this.beforeUnloadHandler = () => {
      this.log('[MQTT] beforeunload/pagehide fired — publishing offline + reaping session');

      // Set flag to prevent any pending operations
      this.isDisconnectingManually = true;

      // Stop heartbeat first
      this.stopPresenceHeartbeat();

      // Best-effort publish - if it fails, LWT will handle it
      this.publishPresenceOffline();

      // Ghost GC: reap THIS session immediately so a reload (which mints a fresh clientId) doesn't
      // leave a 2h ghost queuing into a full mqueue. Best-effort; the short sessionExpiryInterval
      // reaps it shortly after if the DISCONNECT doesn't flush before unload.
      this.reapSessionGracefully();
    };

    // pagehide fires in cases where beforeunload may not (bfcache, mobile) — same handler, idempotent.
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('pagehide', this.beforeUnloadHandler);
    this.log('[MQTT] Registered beforeunload/pagehide handlers');
  }

  private unregisterBeforeUnloadHandler(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      window.removeEventListener('pagehide', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  /**
   * Graceful ghost GC: on tab close/reload, tell the broker to drop THIS session immediately by
   * sending an MQTT5 DISCONNECT with sessionExpiryInterval=0 (overrides the 120s from CONNECT).
   * Without this, every reload leaves a session alive for the full sessionExpiryInterval, queuing
   * downstream deltas into a 1000-cap mqueue it will never drain. Best-effort — guarded against the
   * beforeunload+pagehide double-fire; if the packet doesn't flush before unload, the short
   * sessionExpiryInterval reaps it shortly after.
   */
  private reapSessionGracefully(): void {
    const client = this.client as any;
    if (!client || this.sessionReaped) return;
    this.sessionReaped = true;
    try {
      client.end(false, { reasonCode: 0, properties: { sessionExpiryInterval: 0 } });
    } catch (e) {
      console.warn('[MQTT.diag] reapSessionGracefully: end() threw', (e as Error)?.message);
    }
  }

  /**
   * Periodically evict engines from Redux whose ts is older than the
   * staleness threshold. Heartbeat publishes every 30s, retained message
   * expires in 240s, so anything older than 5 minutes is definitely dead.
   */
  private startStaleEngineSweep(): void {
    this.stopStaleEngineSweep();

    const STALE_THRESHOLD_S = 300; // 5 minutes in seconds
    const SWEEP_INTERVAL_MS = 60_000; // check every 60 seconds

    this.staleEngineTimer = setInterval(() => {
      try {
        const store = getStore();
        if (!store) return;

        const sockets = store.getState()?.ConnectedEnginesReducer?.sockets || [];
        const nowS = Math.floor(Date.now() / 1000);

        for (const socket of sockets) {
          // Skip our own client
          if (socket.id === this.currentClientId) continue;

          const ts = socket.ts;
          if (ts && typeof ts === 'number' && (nowS - ts) > STALE_THRESHOLD_S) {
            console.warn(`[MQTT] Evicting stale engine ${socket.id} (last seen ${nowS - ts}s ago)`);
            store.dispatch(removeClient({ clientId: socket.id }));
          }
        }
      } catch (e) {
        console.error('[MQTT] Error in stale engine sweep:', e);
      }
    }, SWEEP_INTERVAL_MS);

    this.log('[MQTT] Started stale engine sweep (60s interval, 5min threshold)');
  }

  private stopStaleEngineSweep(): void {
    if (this.staleEngineTimer) {
      clearInterval(this.staleEngineTimer);
      this.staleEngineTimer = null;
    }
  }

  /**
   * Start connection health check that monitors for stuck connections.
   * If no successful publish in 2 minutes despite being "connected", force reconnect.
   */
  private startConnectionHealthCheck(): void {
    this.stopConnectionHealthCheck();

    const HEALTH_CHECK_INTERVAL_MS = 60_000; // Check every 60 seconds
    const STALE_CONNECTION_THRESHOLD_MS = 120_000; // 2 minutes without successful publish

    this.connectionHealthTimer = setInterval(() => {
      try {
        // Periodic health-check noise; gate the "everything is fine" tick
        // behind localStorage.BT_MQTT_DIAG=1. Real anomalies below
        // (limbo state, WS dead) still log unconditionally.
        const diag = typeof localStorage !== 'undefined' && localStorage.getItem('BT_MQTT_DIAG') === '1';

        // Skip if not supposed to be connected
        if (!this.client || !this.userId) {
          if (diag) {
            console.warn('[MQTT.diag] healthCheck tick: skipping — no client or userId', {
              hasClient: !!this.client,
              hasUserId: !!this.userId,
            });
          }
          return;
        }

        const now = Date.now();
        const timeSinceLastPublish = now - this.lastSuccessfulPublish;
        const wsDead = this.isWebSocketDead();

        if (diag) {
          console.warn('[MQTT.diag] healthCheck tick', {
            connected: this.client.connected,
            reconnecting: this.client.reconnecting,
            disconnecting: this.client.disconnecting,
            isDisconnectingManually: this.isDisconnectingManually,
            wsDead,
            secSinceLastPublish: Math.round(timeSinceLastPublish / 1000),
          });
        }

        // Limbo-state detector: client is neither connected nor reconnecting, and
        // we are not manually disconnecting. mqtt.js auto-reconnect should be
        // running — if it's not, we're stuck. Force a reconnect.
        if (
          !this.client.connected &&
          !this.client.reconnecting &&
          !this.client.disconnecting &&
          !this.isDisconnectingManually
        ) {
          console.warn('[MQTT.diag] healthCheck tick: LIMBO STATE — client not connected, not reconnecting, not disconnecting. Forcing reconnect.');
          this.forceReconnect();
          return;
        }

        // If the WebSocket is dead but flags say otherwise, force reconnect
        if (wsDead && !this.isDisconnectingManually) {
          console.warn('[MQTT.diag] healthCheck tick: WebSocket dead but flags stale — forcing reconnect');
          this.forceReconnect();
          return;
        }

        // If client says it's connected but no successful publish in threshold time
        if (this.client.connected && timeSinceLastPublish > STALE_CONNECTION_THRESHOLD_MS) {
          console.warn(`[MQTT] Connection health check: No successful publish in ${Math.round(timeSinceLastPublish / 1000)}s - connection may be stuck`);

          // Try to publish presence and see if it works
          this.publishPresenceOnline();

          // If still no publish after another 30s, force reconnect
          setTimeout(() => {
            const newTimeSincePublish = Date.now() - this.lastSuccessfulPublish;
            if (newTimeSincePublish > STALE_CONNECTION_THRESHOLD_MS) {
              console.error(`[MQTT] Connection health check FAILED - forcing reconnection`);
              this.forceReconnect();
            }
          }, 30000);
        }
      } catch (e) {
        console.error('[MQTT] Error in connection health check:', e);
      }
    }, HEALTH_CHECK_INTERVAL_MS);

    this.log('[MQTT] Started connection health check (60s interval, 2min threshold)');
  }

  private stopConnectionHealthCheck(): void {
    if (this.connectionHealthTimer) {
      clearInterval(this.connectionHealthTimer);
      this.connectionHealthTimer = null;
    }
  }

  /** Tear the current client down fully and reconnect ONLY after end() completes, so the broker
   *  registers the close before the same stable clientId returns. Generation-guarded so a
   *  superseded reconnect (a newer connect/forceReconnect) stands down — preventing the overlap
   *  that turns a stable clientId into an EMQX self-takeover war. */
  private forceReconnect(): void {
    // Single reconnect manager: this IS the reconnect path (mqtt.js auto-reconnect is disabled), so
    // there is no internal loop to defer to. An explicit call supersedes any scheduled attempt.
    this.clearReconnectTimer();
    if (!this.userId || !this.config) {
      console.error('[MQTT] forceReconnect: missing userId or config — aborting');
      return;
    }
    const userId = this.userId;
    const password = this.config.password;
    const myGeneration = this.nextGeneration(); // supersede any prior in-flight teardown/reconnect

    let proceeded = false;
    const proceed = () => {
      if (proceeded) return;
      proceeded = true;
      if (myGeneration !== this.connectGeneration) return; // a newer connect/forceReconnect took over
      this.connect(userId, password);
    };

    this.cleanupClient(proceed); // stops timers + removes listeners + ends socket; fires proceed on end
    this.client = null;
    this.currentClientId = null;
    this.hasEverConnected = false;

    setTimeout(proceed, 2000); // safety net if end()'s callback never fires
  }

  /**
   * Re-fetch live data from the DB after a reconnect whose offline gap exceeded the session-expiry
   * window (so the broker reaped our session and queued deltas were lost). Reuses the SAME load thunks
   * the post-auth bootstrap runs — messages + campaigns (calendar / message status) and sessions
   * (engine / QR state). Dynamically imported to avoid the module cycle (matches the auth handler).
   * Per cluster telemetry only ~1.5% of reconnects are reaped, so this is rare + cheap.
   */
  private resyncAfterGap(sessionPresent: boolean | undefined): void {
    // Debounce: a reconnect flap (or a resync coinciding with a re-auth-driven post-auth load) must
    // not fire the refetch repeatedly. One resync per window is enough to catch up missed deltas.
    const now = Date.now();
    if (isWithinDebounce(this.lastResyncAt, now, MQTTSubscriber.RESYNC_DEBOUNCE_MS)) {
      console.warn('[MQTT.diag] resyncAfterGap: debounced (resynced moments ago)');
      return;
    }
    this.lastResyncAt = now;
    console.warn(`[MQTT.diag] reconnect with sessionPresent=${sessionPresent} — broker had no session, resyncing live data from DB`);
    void (async () => {
      try {
        const { loadUserMessagesAndCampaignsThunk } = await import(/* webpackMode: "eager" */ '@common-reducers/UserThunks');
        const { DBSessionThunks } = await import(/* webpackMode: "eager" */ '@common-reducers/DBServiceThunks');
        // skipIfLoadedWithinMs collapses this with a near-simultaneous post-auth/component load so we
        // don't double-fetch (e.g. a >120s reconnect landing next to a storage-sync re-auth).
        safeDispatch(loadUserMessagesAndCampaignsThunk({ skipIfLoadedWithinMs: MQTTSubscriber.RESYNC_DEBOUNCE_MS }));
        safeDispatch(DBSessionThunks.find({}));
      } catch (e) {
        console.warn('[MQTT.diag] resyncAfterGap: dispatch failed', (e as Error)?.message);
      }
    })();
  }

  private setupEventHandlers(): void {
    if (!this.client) return;

    this.log('[MQTT] Setting up event handlers');

    // Diag-gated broker signal: CONNACK return code (0=accepted, 4=bad user/pass, 5=not authorized).
    if (typeof localStorage !== 'undefined' && localStorage.getItem('BT_MQTT_DIAG') === '1') {
      this.client.on('packetreceive', (packet) => {
        if (packet.cmd === 'connack') {
          console.warn('[MQTT.diag] CONNACK ' + JSON.stringify({ returnCode: packet.returnCode, sessionPresent: packet.sessionPresent }));
        }
      });
    }

    this.client.on('connect', (connack?: { sessionPresent?: boolean }) => {
      this.connState = 'connected';
      this.hasEverConnected = true;
      this.connackCount++;
      this.log('[MQTT] Connected to broker. Client ID:', this.currentClientId);
      this.isConnecting = false; // Reset connection flag on success

      // CONNACK arrived — the storm is over. Reset backoff so the NEXT disconnect retries fast,
      // and cancel any pending scheduled attempt (we are connected; it would only churn).
      this.reconnectAttempt = 0;
      this.clearReconnectTimer();
      this.sessionReaped = false;

      // Gap-resync, decided by the broker's CONNACK sessionPresent (authoritative — no client timer).
      // sessionPresent=false on a RECONNECT means the broker had no session for us (reaped past expiry,
      // taken over, or lost to a restart), so deltas published while we were away are gone → refetch
      // live data. sessionPresent=true means it resumed and is replaying the queue → nothing missed.
      if (shouldResyncOnConnect(this.connackCount, connack?.sessionPresent)) {
        this.resyncAfterGap(connack?.sessionPresent);
      }
      // Initialize last successful publish timestamp
      this.lastSuccessfulPublish = Date.now();

      // Update Redux connection status to AUTHENTICATED
      this.updateConnectionStatus('AUTHENTICATED');

      this.subscribeToTopics();

      // Publish presence online
      this.publishPresenceOnline();

      // Start presence heartbeat to keep presence fresh
      this.startPresenceHeartbeat();

      // Start connection health check to detect stuck connections
      this.startConnectionHealthCheck();

      // Register beforeunload handler so tab/browser close publishes offline
      this.registerBeforeUnloadHandler();

      // Start periodic sweep to evict stale engines from Redux
      this.startStaleEngineSweep();

      // Start alarm-based keepalive to bypass Chrome background tab throttling
      this.setupAlarmKeepalive();

      // NOTE: queryConnectedClients() has been removed because:
      // 1. Backend returns empty array for backward compatibility
      // 2. Client tracking is now handled via MQTT presence messages
      // 3. Calling it would overwrite the presence list with an empty array
      // Presence is tracked exclusively via MQTT presence channel subscriptions
    });

    this.client.on('disconnect', (packet) => {
      console.warn('[MQTT.diag] event:disconnect — broker sent disconnect packet', packet);
      // Update Redux connection status
      this.updateConnectionStatus('DISCONNECTED');
    });

    this.client.on('error', (error) => {
      console.warn('[MQTT.diag] event:error', {
        message: error.message,
        connected: this.client?.connected,
        reconnecting: this.client?.reconnecting,
      });
      this.isConnecting = false; // Reset connection flag on error

      // Handle specific error types
      if (error.message && error.message.includes('connack timeout')) {
        console.error('[MQTT] Connection acknowledgment timeout - broker may be overloaded or unreachable');
      } else if (error.message && error.message.includes('Connection refused')) {
        console.error('[MQTT] Connection refused - check credentials or broker availability');
      }
    });

    this.client.on('offline', () => {
      console.warn('[MQTT.diag] event:offline', {
        connected: this.client?.connected,
        reconnecting: this.client?.reconnecting,
        disconnecting: this.client?.disconnecting,
      });
      this.updateConnectionStatus('DISCONNECTED');

      // Only an ESTABLISHED connection that genuinely dropped may force a reconnect. While we are
      // still handshaking (connState === 'connecting', no CONNACK yet) an 'offline' is part of the
      // connect/close churn — forcing a reconnect here starts a SECOND connect that overlaps the
      // in-flight one and (with the same stable clientId) triggers an EMQX takeover war.
      if (this.connState !== 'connected' || !this.hasEverConnected) {
        return;
      }
      if (this.isWebSocketDead()) {
        console.warn('[MQTT.diag] event:offline — WebSocket dead, forcing full reconnect');
        this.forceReconnect();
      }
    });

    this.client.on('reconnect', () => {
      // reconnectPeriod is 0 — mqtt.js must NEVER self-reconnect. If this fires, an internal timer
      // survived somewhere (the exact ghost-client mechanism behind the 2026-07-05 takeover war).
      console.error('[MQTT.diag] INVARIANT VIOLATION: mqtt.js internal reconnect fired with reconnectPeriod:0 — investigate');
    });

    this.client.on('close', () => {
      console.warn('[MQTT.diag] event:close', {
        connected: this.client?.connected,
        reconnecting: this.client?.reconnecting,
        disconnecting: this.client?.disconnecting,
      });
      // Don't update status here - 'offline' event handles it

      // A handshake that closes before ever connecting must release the single-flight guards so a
      // later connect() (e.g. re-login with a fresh token) can retry — otherwise the surface wedges
      // in 'connecting' forever. Do NOT forceReconnect here (that is the takeover war); just free the
      // state. mqtt.js's own reconnectPeriod still retries a transient failure on the existing client.
      if (this.connState === 'connecting' && !this.hasEverConnected) {
        this.connState = 'disconnected';
        this.isConnecting = false;
      }

      // Single reconnect manager: mqtt.js never auto-reconnects (reconnectPeriod: 0), so a close on
      // the LIVE client (manual teardown removes listeners first, so this never fires for those) is
      // terminal for that socket — recovery is OURS. Dedup + backoff live in scheduleReconnect().
      if (!this.isDisconnectingManually) {
        this.scheduleReconnect('close');
      }
    });

    this.client.on('message', (topic, message) => {
      try {
        const messageString = message.toString();
        let messageData: any;
        if (messageString === "") {
          // Presence topics use empty payload to signal offline — pass null
          // so handlePresenceMessage can detect it and call removeClient.
          // Non-presence topics get a { status: 'disconnected' } object.
          if (topic.includes('/presence/')) {
            messageData = null;
          } else {
            messageData = { status: 'disconnected' };
          }
        } else {
          messageData = JSON.parse(messageString);
        }

        // Handle regular application messages (including client events)
        this.handleMessage(topic, messageData);
      } catch (error) {
        console.error('[MQTT] Error parsing or handling message:', error, "Topic:", topic, "Message:", message.toString());
      }
    });
  }

  private subscribeToTopics(): void {
    if (!this.client || !this.userId) return;

    const userTopics = [
      `users/${this.userId}/downstream/+`,  // General user updates (including client-events)
      `users/${this.userId}/requests/+`, // Request topics for request-response pattern (legacy)
      `clients/${this.currentClientId}/requests/+`, // Client-specific request topics (new)
      `global/broadcast/+`, // Global broadcast topics for settings and other system-wide events
      `users/${this.userId}/presence/#`, // Presence channel for online/offline tracking
    ];

    userTopics.forEach(topic => {
      this.client?.subscribe(topic, (err) => {
        if (err) {
          console.error(`[MQTT] Failed to subscribe to ${topic}:`, err);
        } else {
          this.log(`[MQTT] Subscribed to ${topic}`);
          this.subscribedTopics.add(topic);
        }
      });
    });
  }

  private handleMessage(topic: string, messageData: any): void {
    this.log(`[MQTT] Received message on topic ${topic}:`, messageData);

    // Handle request-response pattern
    if (topic.includes('/requests/')) {
      this.handleRequest(topic, messageData);
      return;
    }

    // Handle client connection events
    if (topic.endsWith('/client-events')) {
      this.handleClientEvents(topic, messageData);
      return;
    }

    // Handle presence messages
    if (topic.includes('/presence/')) {
      this.handlePresenceMessage(topic, messageData);
      return;
    }

    // Map MQTT topic to Socket.IO event type for regular updates
    const eventType = this.extractEventTypeFromTopic(topic);

    if (eventType) {
      this.log(`[MQTT] Mapped topic ${topic} to event type: ${eventType}`);

      // Handle global broadcast messages specially
      if (eventType === 'global-broadcast') {
        // For global broadcast messages, the actual event type is in the messageData.eventType
        const actualEventType = messageData.eventType;
        if (actualEventType) {
          this.log(`[MQTT] Global broadcast event type: ${actualEventType}`);

          // Convert MQTT message to Socket.IO format and extract the data
          const socketIOMessage = this.convertToSocketIOFormat(messageData.data || messageData, actualEventType);

          // Trigger the appropriate handler
          const handler = this.messageHandlers.get(actualEventType);
          if (handler) {
            this.log(`[MQTT] Dispatching global broadcast ${actualEventType} event to handler`);
            handler(socketIOMessage);
          } else {
            console.warn(`[MQTT] No handler found for global broadcast event type: ${actualEventType}`);
          }
        } else {
          console.warn(`[MQTT] Global broadcast message missing eventType:`, messageData);
        }
        return;
      }

      // Convert MQTT message to Socket.IO format
      const socketIOMessage = this.convertToSocketIOFormat(messageData, eventType);

      // Trigger the appropriate handler
      const handler = this.messageHandlers.get(eventType);
      if (handler) {
        this.log(`[MQTT] Dispatching ${eventType} event to handler`);
        handler(socketIOMessage);
      } else {
        console.warn(`[MQTT] No handler found for event type: ${eventType}`);
      }
    } else {
      console.warn(`[MQTT] Could not extract event type from topic: ${topic}`);
    }
  }

  private handleRequest(topic: string, requestData: any): void {
    this.log(`[MQTT] Handling request on topic ${topic}:`, requestData);

    // Extract requestId from topic patterns:
    // - users/{userId}/requests/{requestId} (legacy)
    // - clients/{clientId}/requests/{requestId} (new)
    const parts = topic.split('/');
    let requestId: string;

    if (parts[0] === 'users' && parts[2] === 'requests') {
      requestId = parts[3]; // Legacy format
    } else if (parts[0] === 'clients' && parts[2] === 'requests') {
      requestId = parts[3]; // New format
      this.log(`[MQTT] Received client-specific request for ${parts[1]}`);
    } else {
      console.error(`[MQTT] Unknown request topic format: ${topic}`);
      return;
    }

    if (!requestId || !requestData.command) {
      console.error(`[MQTT] Invalid request format:`, { requestId, command: requestData.command });
      return;
    }

    // [PROD-BACKPORT reply-to-MQTT] Capture replyTo from the request envelope so responses
    // are routed to the originating backend replica's per-replica topic instead of the
    // shared 5-level topic. The backend stamps replyTo; absent = fall back to legacy topic.
    const replyTo = typeof requestData.replyTo === 'string' ? requestData.replyTo : undefined;

    // Handle the request and send response (no filtering needed for client-specific topics)
    this.processRequest(requestId, requestData.command, requestData.params || {}, replyTo);
  }

  // [PROD-BACKPORT reply-to-MQTT] Accept replyTo to thread it through to every publishResponse call.
  private async processRequest(requestId: string, command: string, params: any, replyTo?: string): Promise<void> {
    // Always log for query_contacts_and_chats to debug the limit issue
    if (command === 'query_contacts_and_chats') {
      console.log('[LIMIT_DEBUG_1] [MQTTSubscriber] params:', JSON.stringify(params));
      console.log('[LIMIT_DEBUG_1] [MQTTSubscriber] params.limit:', params?.limit, 'type:', typeof params?.limit);
    }
    // Use Number() conversion to handle string "-1" that may come through MQTT/JSON serialization
    const isLimitMinus1 = params?.limit !== undefined && Number(params.limit) === -1;
    if (isLimitMinus1) {
      console.log(`[DEBUG_LIMIT-1] [processRequest] START - requestId: ${requestId}, command: ${command}`);
      console.log(`[DEBUG_LIMIT-1] [processRequest] params:`, JSON.stringify(params));
      console.log(`[DEBUG_LIMIT-1] [processRequest] params.limit type: ${typeof params.limit}, value: ${params.limit}`);
    }

    // Diagnostic: trace where server-mode tool requests get stuck.
    const processStart = Date.now();
    logMqttSubscriberEvent('WA_TAB_MQTT_REQUEST_RECEIVED', { requestId, command });

    try {
      this.log(`[MQTT] Processing request ${requestId}: ${command}`);

      // #236: CONTEXT GUARD BEFORE THE LOCK. Only the context that will actually
      // execute (the WhatsApp Web tab) may proceed. Previously the remote lock ran
      // first, so a non-executing context (Dashboard / second tab) could WIN the
      // lock and then bail here — leaving the real WhatsApp tab to get acquired:false
      // and silently drop the command. Guarding first also stops the N-context
      // fan-out that floods /actions?cmd=check-engine-cmd-lock.
      // IMPORTANT: Do NOT send a response - the WhatsApp client will respond instead.
      if (!isWhatsappExtensionContext()) {
        this.log(`Silently ignoring ${command} request in non-WhatsApp Web context (letting WhatsApp client respond)`);
        return;
      }

      const extensionEngineCmdId =
        params && typeof (params as Record<string, unknown>)._engineCmdId === 'string'
          ? (params as { _engineCmdId: string })._engineCmdId
          : null;

      // #236 (3): LOCAL dedup of repeat MQTT deliveries of the same _engineCmdId
      // (~3× per engine via the shared user-level topic + per-client topic /
      // backend republish). Drop the 2nd/3rd here — no re-execution, no publish, no
      // network. Unlike the remote lock's empty `deduplicated:true`, we publish
      // NOTHING, so the FIRST delivery's real result is what the caller receives
      // (safe for read commands too).
      if (extensionEngineCmdId && this.engineCmdDedup.markSeen(extensionEngineCmdId)) {
        this.log(`[engine-cmd-dedup] duplicate delivery of ${command} (${extensionEngineCmdId}) — dropping`);
        return;
      }

      // #236 (2): REMOTE engine-cmd lock ONLY for STATE-CHANGING commands
      // (send / delete / group mutation). Read-only tools and the idempotent
      // insertOnlyIfMissing store-probe (≈99% of the observed volume) skip the
      // HTTP+Redis round-trip entirely. Lock denied → short-circuit with dedup
      // response, never invoke handler. Fail-open on endpoint error (BE#54).
      if (extensionEngineCmdId && isStateChangingEngineCommand(command)) {
        try {
          if (!this.backendActionsClient) {
            this.backendActionsClient = createExtensionBackendActionsClient();
          }
          const lockResp = await this.backendActionsClient.checkEngineCmdLock({
            engineCmdId: extensionEngineCmdId,
          });
          if (lockResp && lockResp.acquired === false) {
            console.log('[engine-cmd-lock] engine-side dedup blocked', {
              command,
              engineCmdId: extensionEngineCmdId,
              requestId,
            });
            this.publishResponse(requestId, { // [PROD-BACKPORT reply-to-MQTT] pass replyTo for per-replica routing
              success: true,
              data: {
                deduplicated: true,
                lockId: extensionEngineCmdId,
                reason: 'engine-cmd-lock',
              },
            }, replyTo);
            return;
          }
          if (lockResp === undefined) {
            // FAIL-OPEN — backend unreachable; proceed with handler.
            console.warn('[engine-cmd-lock] check failed, proceeding without protection', {
              command,
              engineCmdId: extensionEngineCmdId,
            });
          }
        } catch (err: unknown) {
          // Defensive fail-open — if the helper somehow throws, don't block.
          const message = err instanceof Error ? err.message : String(err);
          console.warn('[engine-cmd-lock] check threw, proceeding without protection', {
            command,
            engineCmdId: extensionEngineCmdId,
            message,
          });
        }
      }

      // Create a callback function that publishes the response
      const responseCallback = (response: any) => {
        if (isLimitMinus1) {
          console.log(`[DEBUG_LIMIT-1] [processRequest] responseCallback called - has data: ${!!response?.data}, success: ${response?.success}`);
          if (response?.data?.length !== undefined) {
            console.log(`[DEBUG_LIMIT-1] [processRequest] responseCallback data length: ${response.data.length}`);
          }
        }
        logMqttSubscriberEvent('WA_TAB_MQTT_RESPONSE_PUBLISH', {
          requestId,
          command,
          durationMs: Date.now() - processStart,
          success: !!response?.success,
          error: response?.success ? undefined : response?.error,
        });
        this.publishResponse(requestId, response, replyTo); // [PROD-BACKPORT reply-to-MQTT] pass replyTo for per-replica routing
      };

      // Create the request event in the same format as the Socket.IO version for whatsapp-interface
      const requestEvent = {
        path: 'whatsapp-interface',
        method: command,
        entity: {
          params,
          owner: this.userId
        }
      };

      // Handle WhatsApp interface requests directly
      if (isLimitMinus1) {
        console.log(`[DEBUG_LIMIT-1] [processRequest] Calling handleWhatsappInterfaceRequest...`);
        const startTime = Date.now();
        await handleWhatsappInterfaceRequest(requestEvent, responseCallback, this.log.bind(this));
        console.log(`[DEBUG_LIMIT-1] [processRequest] handleWhatsappInterfaceRequest returned in ${Date.now() - startTime}ms`);
      } else {
        await handleWhatsappInterfaceRequest(requestEvent, responseCallback, this.log.bind(this));
      }
    } catch (error) {
      console.error(`[MQTT] Error processing request ${requestId}:`, error);
      // The taxonomy error object, never a bare string (5.8a maps `code`).
      this.publishResponse(requestId, waToolError( // [PROD-BACKPORT reply-to-MQTT] pass replyTo for per-replica routing
        error instanceof EngineToolError ? error.code : 'engine_error',
        (error as { message?: string })?.message || 'Unknown error',
      ), replyTo);
    }
  }

  // Maximum chunk size for MQTT messages (400KB to stay well under WebSocket limits)
  private readonly CHUNK_SIZE_BYTES = 400 * 1024;
  // Threshold above which to use chunking (500KB)
  private readonly CHUNK_THRESHOLD_BYTES = 500 * 1024;

  /**
   * [PROD-BACKPORT reply-to-MQTT]
   * WHAT: Publish engine tool-responses to a per-replica 6-level topic
   *   (users/{userId}/upstream/responses/<replyTo>/<reqId>) when the request envelope
   *   carries a `replyTo` field (the backend now stamps the replica ID there).
   *   Without replyTo, falls back to the legacy 5-level topic so old senders still work.
   *   This eliminates the response fan-out/overflow where every backend replica received
   *   every response and all tried to handle it, overwhelming EMQX.
   *
   * STAGING-MERGE NOTE: staging implements responses entirely differently — HTTP relay
   *   via the Service Worker + publishResponseViaMqtt / deliverResponse.  When staging
   *   is eventually merged into prod, TAKE STAGING'S VERSION and DROP these backport
   *   lines — do NOT double-apply.
   */
  private publishResponse(requestId: string, response: any, replyTo?: string): void { // [PROD-BACKPORT reply-to-MQTT] added replyTo param
    console.log(`[DEBUG_LIMIT-1] [publishResponse] START - requestId: ${requestId}`);
    console.log(`[DEBUG_LIMIT-1] [publishResponse] Response success: ${response?.success}, has data: ${!!response?.data}`);
    if (response?.data?.length !== undefined) {
      console.log(`[DEBUG_LIMIT-1] [publishResponse] Response data array length: ${response.data.length}`);
    }

    if (!this.client) {
      console.error(`[MQTT] Cannot publish response - client not connected`);
      return;
    }

    // With clean:false, queued messages may arrive before the 'connect' event fires.
    // Defer publishing the response until the client is fully connected.
    // Also check for disconnecting state to avoid race conditions.
    if (!this.isClientReadyForPublish()) {
      // If we're disconnecting, don't bother deferring
      if (this.isDisconnectingManually || this.client.disconnecting) {
        this.log(`[MQTT] Dropping response for ${requestId} - client is disconnecting`);
        console.warn(`[DEBUG_LIMIT-1] [publishResponse] DROPPING response - client disconnecting`);
        return;
      }

      this.log(`[MQTT] Deferring response for ${requestId} - client reconnecting`);
      console.log(`[DEBUG_LIMIT-1] [publishResponse] Deferring response - client reconnecting`);
      const onConnect = () => {
        this.client?.removeListener('connect', onConnect);
        this.publishResponse(requestId, response, replyTo); // [PROD-BACKPORT reply-to-MQTT] propagate replyTo into the deferred re-publish
      };
      this.client.on('connect', onConnect);
      return;
    }

    // [PROD-BACKPORT reply-to-MQTT] Build a depth-aware response topic:
    //   - replyTo present → 6-level per-replica topic: …/responses/<replyTo>/<reqId>
    //     so only the originating backend replica's MQTT subscription matches.
    //   - replyTo absent  → 5-level legacy topic: …/responses/<reqId> (backward compat).
    const responseTopic = replyTo
      ? `users/${this.userId}/upstream/responses/${replyTo}/${requestId}`
      : `users/${this.userId}/upstream/responses/${requestId}`;
    const responseMessage = {
      requestId,
      success: response.success !== false, // Default to true unless explicitly false
      data: response.success !== false ? response.data : undefined, // Extract just the data field
      error: response.success === false ? response.error : undefined,
      _timestamp: Date.now()
    };

    console.log(`[DEBUG_LIMIT-1] [publishResponse] Serializing response message...`);
    let message: string;
    try {
      message = JSON.stringify(responseMessage);
      console.log(`[DEBUG_LIMIT-1] [publishResponse] Serialization OK - size: ${message.length} bytes (${(message.length / 1024 / 1024).toFixed(2)} MB)`);
    } catch (jsonError) {
      console.error(`[DEBUG_LIMIT-1] [publishResponse] JSON.stringify FAILED:`, jsonError);
      throw jsonError;
    }

    // Check if we need to use chunked response
    if (message.length > this.CHUNK_THRESHOLD_BYTES) {
      console.log(`[DEBUG_LIMIT-1] [publishResponse] Message too large (${message.length} bytes), using chunked response`);
      this.publishChunkedResponse(requestId, responseTopic, message);
      return;
    }

    console.log(`[DEBUG_LIMIT-1] [publishResponse] Calling client.publish to topic: ${responseTopic}`);
    try {
      this.client.publish(responseTopic, message, { qos: 1 }, (error) => {
        if (error) {
          // Don't log as error if we're disconnecting
          if (this.isDisconnectingManually || this.client?.disconnecting) {
            this.log(`[MQTT] Response publish skipped during disconnect`);
            console.warn(`[DEBUG_LIMIT-1] [publishResponse] Publish skipped during disconnect`);
            logMqttSubscriberEvent('WA_TAB_MQTT_PUBLISH_ACK', { requestId, topic: responseTopic, bytes: message.length, success: false, error: 'skipped_during_disconnect' });
          } else {
            console.warn(`[MQTT] Failed to publish response to ${responseTopic}:`, error.message);
            console.error(`[DEBUG_LIMIT-1] [publishResponse] Publish FAILED:`, error);
            logMqttSubscriberEvent('WA_TAB_MQTT_PUBLISH_ACK', { requestId, topic: responseTopic, bytes: message.length, success: false, error: error.message });
          }
        } else {
          this.log(`[MQTT] Published response for request ${requestId} to ${responseTopic}`);
          console.log(`[DEBUG_LIMIT-1] [publishResponse] Publish SUCCESS - requestId: ${requestId}`);
          logMqttSubscriberEvent('WA_TAB_MQTT_PUBLISH_ACK', { requestId, topic: responseTopic, bytes: message.length, success: true });
        }
      });
    } catch (error) {
      this.log(`[MQTT] Caught error during response publish:`, error);
      console.error(`[DEBUG_LIMIT-1] [publishResponse] Publish threw exception:`, error);
    }
  }

  /**
   * Publishes a large response in chunks to avoid WebSocket disconnections.
   * Each chunk is sent as a separate MQTT message with metadata for reassembly.
   */
  private publishChunkedResponse(requestId: string, responseTopic: string, fullMessage: string): void {
    const totalChunks = Math.ceil(fullMessage.length / this.CHUNK_SIZE_BYTES);
    console.log(`[DEBUG_LIMIT-1] [publishChunkedResponse] Splitting into ${totalChunks} chunks of ~${this.CHUNK_SIZE_BYTES} bytes`);

    let chunkIndex = 0;

    const publishNextChunk = () => {
      if (chunkIndex >= totalChunks) {
        console.log(`[DEBUG_LIMIT-1] [publishChunkedResponse] All ${totalChunks} chunks published successfully`);
        return;
      }

      if (!this.client || !this.isClientReadyForPublish()) {
        console.error(`[DEBUG_LIMIT-1] [publishChunkedResponse] Client not ready, stopping at chunk ${chunkIndex}/${totalChunks}`);
        return;
      }

      const start = chunkIndex * this.CHUNK_SIZE_BYTES;
      const end = Math.min(start + this.CHUNK_SIZE_BYTES, fullMessage.length);
      const chunkData = fullMessage.slice(start, end);

      const chunkMessage = JSON.stringify({
        requestId,
        isChunked: true,
        chunkIndex,
        totalChunks,
        chunkData,
        _timestamp: Date.now()
      });

      console.log(`[DEBUG_LIMIT-1] [publishChunkedResponse] Publishing chunk ${chunkIndex + 1}/${totalChunks} (${chunkData.length} bytes)`);

      this.client.publish(responseTopic, chunkMessage, { qos: 1 }, (error) => {
        if (error) {
          if (this.isDisconnectingManually || this.client?.disconnecting) {
            console.warn(`[DEBUG_LIMIT-1] [publishChunkedResponse] Chunk ${chunkIndex} skipped during disconnect`);
          } else {
            console.error(`[DEBUG_LIMIT-1] [publishChunkedResponse] Failed to publish chunk ${chunkIndex}:`, error);
          }
          return;
        }

        console.log(`[DEBUG_LIMIT-1] [publishChunkedResponse] Chunk ${chunkIndex + 1}/${totalChunks} published successfully`);
        chunkIndex++;

        // Use queueMicrotask instead of setTimeout to avoid Chrome's background tab throttling
        // Chrome throttles setTimeout to 1 call/minute in background tabs after 5 minutes,
        // but queueMicrotask is not throttled. The MQTT publish callback already provides
        // enough back-pressure to avoid overwhelming the connection.
        queueMicrotask(publishNextChunk);
      });
    };

    publishNextChunk();
  }

  private extractEventTypeFromTopic(topic: string): string | null {
    // Extract event type from topic patterns
    // users/{userId}/downstream/{path} -> "update" for entity updates, specific types for system events
    // users/{userId}/downstream/boards/{boardId} -> "update" (board update)
    // users/{userId}/downstream/workspaces/{workspaceId} -> "update" (workspace update)
    // users/{userId}/downstream/client-events -> "client-events" (client connection events)
    // boards/{boardId}/downstream -> "update" (direct board update)
    // workspaces/{workspaceId}/downstream -> "update" (direct workspace update)
    // global/broadcast/{eventType} -> map to the eventType from the message data

    const parts = topic.split('/');

    // Handle global broadcast topics
    if (parts[0] === 'global' && parts[1] === 'broadcast') {
      // For global broadcast topics, we need to extract the event type from the message data
      // The backend publishes with eventType in the message payload
      return 'global-broadcast'; // We'll handle this specially in the message handler
    }

    // Handle direct board and workspace topics
    if (parts[0] === 'boards' && parts[2] === 'downstream') {
      return 'update'; // Direct board update
    }

    if (parts[0] === 'workspaces' && parts[2] === 'downstream') {
      return 'update'; // Direct workspace update
    }

    // Handle user-specific topics
    if (parts[0] === 'users' && parts[2] === 'downstream') {
      if (parts[3] === 'boards') {
        return 'update'; // Board update
      } else if (parts[3] === 'workspaces') {
        return 'update'; // Workspace update  
      } else if (parts[3] === 'client-events') {
        return 'client-events'; // Client connection events
      } else {
        // Check if this is a special system event
        const eventType = parts[3];
        if (['sync-user-data', 'chatbot-progress', 'document-processing-progress', 'send-message', 'wa-message-listener-event'].includes(eventType)) {
          return eventType; // Special system events
        } else {
          return 'update'; // All other entity updates (tasks, notifications, etc.)
        }
      }
    }

    if (parts[0] === 'admin' && parts[1] === 'dashboard' && parts[2] === 'downstream') {
      return 'update'; // Admin dashboard events should also be "update"
    }

    return null;
  }

  private convertToSocketIOFormat(messageData: any, eventType: string): any {
    // Convert MQTT message format to match Socket.IO event expectations
    // The backend publishes the same data structure, so this should be a pass-through
    return messageData;
  }

  public isConnected(): boolean {
    if (!this.client || !this.client.connected) return false;
    // Don't trust client.connected if the WebSocket is actually dead
    if (this.isWebSocketDead()) return false;
    return true;
  }

  // queryConnectedClients() has been removed
  // Client tracking is now done exclusively via MQTT presence messages
  // Each retained presence message is processed individually via handlePresenceMessage()
  // which uses upsertClient/removeClient actions

  /**
   * Generic method to subscribe to a topic with automatic cleanup of similar topics
   * @param topicPattern - The topic to subscribe to
   * @param entityType - Optional entity type for grouping (e.g., 'boards', 'workspaces')
   */
  public subscribeToTopic(topicPattern: string, entityType?: string): void {
    if (!this.client || !this.userId) return;

    // If entityType is provided, find and unsubscribe from existing topics of the same type
    if (entityType) {
      const existingTopic = Array.from(this.subscribedTopics).find(topic =>
        topic.includes(`/${entityType}/`) && topic !== topicPattern
      );

      if (existingTopic) {
        this.client.unsubscribe(existingTopic, (err) => {
          if (err) {
            console.error(`[MQTT] Failed to unsubscribe from previous ${entityType} topic:`, err);
          } else {
            this.log(`[MQTT] Unsubscribed from previous ${entityType} topic: ${existingTopic}`);
            this.subscribedTopics.delete(existingTopic);
          }
        });
      }
    }

    // Check if already subscribed to this exact topic
    if (this.subscribedTopics.has(topicPattern)) {
      this.log(`[MQTT] Already subscribed to topic: ${topicPattern}`);
      return;
    }

    // Subscribe to new topic
    this.client.subscribe(topicPattern, (err) => {
      if (err) {
        console.error(`[MQTT] Failed to subscribe to topic ${topicPattern}:`, err);
      } else {
        this.log(`[MQTT] Subscribed to topic: ${topicPattern}`);
        this.subscribedTopics.add(topicPattern);
      }
    });
  }

  /**
   * Generic method to unsubscribe from a topic
   */
  public unsubscribeFromTopic(topicPattern: string): void {
    if (!this.client) return;

    if (this.subscribedTopics.has(topicPattern)) {
      this.client.unsubscribe(topicPattern, (err) => {
        if (err) {
          console.error(`[MQTT] Failed to unsubscribe from topic ${topicPattern}:`, err);
        } else {
          this.log(`[MQTT] Unsubscribed from topic: ${topicPattern}`);
          this.subscribedTopics.delete(topicPattern);
        }
      });
    } else {
      this.log(`[MQTT] Not subscribed to topic: ${topicPattern}`);
    }
  }

  /**
   * Convenience method for entity subscriptions (boards, workspaces, etc.)
   */
  public subscribeToEntity(entityType: 'boards' | 'workspaces', entityId: string): void {
    const topicPattern = `${entityType}/${entityId}/downstream`;
    this.subscribeToTopic(topicPattern, entityType);
  }

  /**
   * Get all current subscriptions for debugging
   */
  public getCurrentSubscriptions(): string[] {
    return Array.from(this.subscribedTopics);
  }

  private handleClientEvents(topic: string, messageData: any): void {
    this.log(`[MQTT] Handling client events on topic ${topic}:`, messageData);
    
    // NOTE: Client events are now tracked via MQTT presence messages (users/{userId}/presence/{clientId})
    // This webhook-based handler is kept for backward compatibility but not used for presence tracking
    // Presence is tracked exclusively via handlePresenceMessage() using upsertClient/removeClient actions
    
    this.log(`[MQTT] Client event received but ignored - using MQTT presence tracking instead`);
  }

  /**
   * Handle presence messages from MQTT presence channels
   * Format: users/{userId}/presence/{clientId}
   * Payload: EnginePresence ({ clientId, engineKind, commands, contractVersion, app, ts,
   * browserType (rollout) }) — or the web app's { clientId, browserType, app, ts }
   * 
   * Timestamp format: Unix timestamp in seconds (Math.floor(Date.now() / 1000))
   * All MQTT presence publishers must use this consistent format
   */
  private handlePresenceMessage(topic: string, messageData: any): void {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('BT_MQTT_DIAG') === '1') {
      console.warn('[MQTT.diag] presence message received', topic, messageData ? { ts: messageData.ts, teamInboxOpen: messageData.teamInboxOpen } : '(offline/empty)');
    }
    this.log(`[MQTT] ========================================`);
    this.log(`[MQTT] Handling presence message on topic ${topic}:`, messageData);
    
    // Debug timestamp issues
    if (messageData && messageData.ts) {
      this.log(`[MQTT] Timestamp debug - ts: ${messageData.ts}, type: ${typeof messageData.ts}, valid: ${!isNaN(messageData.ts) && messageData.ts > 0}`);
    }

    // Extract clientId from topic: users/{userId}/presence/{clientId}
    const topicParts = topic.split('/');
    if (topicParts.length !== 4 || topicParts[0] !== 'users' || topicParts[2] !== 'presence') {
      console.error(`[MQTT] Invalid presence topic format: ${topic}`);
      return;
    }

    const clientId = topicParts[3];
    const userId = topicParts[1];

    this.log(`[MQTT] Extracted clientId: ${clientId}, userId: ${userId}`);
    this.log(`[MQTT] My clientId: ${this.currentClientId}`);

    // Check if this is our own presence message (ignore to avoid loops)
    if (clientId === this.currentClientId) {
      this.log(`[MQTT] Ignoring own presence message: ${clientId}`);
      return;
    }

    try {
      const store = getStore();
      const dispatch = store?.dispatch;

      if (!dispatch) {
        console.error('[MQTT] Store dispatch not available for updating presence');
        return;
      }

      // Get current connected clients from Redux store for logging
      const currentState = store.getState();
      const currentClients = currentState?.ConnectedEnginesReducer?.connectedSockets || [];

      this.log(`[MQTT] Current clients before update:`, currentClients.map((c: { id: string }) => c.id));

      if (!messageData || messageData === '' || Object.keys(messageData).length === 0) {
        // Empty payload means client went offline - use removeClient action
        this.log(`[MQTT] Client ${clientId} went offline (empty presence)`);
        dispatch(removeClient({ clientId }));
      } else {
        // Non-empty payload means client is online - use upsertClient action
        this.log(`[MQTT] Client ${clientId} is online - adding/updating`);
        
        // Create ConnectedSocket object from presence data
        // Validate and safely convert timestamp (expects Unix timestamp in seconds)
        let connectionTime: string;
        try {
          if (messageData.ts && typeof messageData.ts === 'number' && messageData.ts > 0) {
            // All MQTT presence publishers use Math.floor(Date.now() / 1000) - Unix timestamp in seconds
            const timestampMs = messageData.ts * 1000;
            
            // Validate timestamp is reasonable (not too far in past/future)
            const now = Date.now();
            const maxPast = 365 * 24 * 60 * 60 * 1000; // 1 year ago
            const maxFuture = 24 * 60 * 60 * 1000; // 1 day in future
            
            if (timestampMs >= (now - maxPast) && timestampMs <= (now + maxFuture)) {
              connectionTime = new Date(timestampMs).toISOString();
            } else {
              console.debug(`[MQTT] Invalid timestamp ${messageData.ts} for client ${clientId}, using current time`);
              connectionTime = new Date().toISOString();
            }
          } else {
            console.debug(`[MQTT] Missing or invalid timestamp for client ${clientId}, using current time`);
            connectionTime = new Date().toISOString();
          }
        } catch (error) {
          console.error(`[MQTT] Error processing timestamp for client ${clientId}:`, error);
          connectionTime = new Date().toISOString();
        }

        const connectedSocket: ConnectedSocket = {
          id: clientId,
          device: messageData.device || 'Desktop',
          browserType: messageData.browserType || 'Unknown',
          connectionTime: connectionTime,
          connectedFrom: 'mqtt',
          // EnginePresence: the record as published (selectOnlineEngines reads it).
          ...(typeof messageData.engineKind === 'string' ? { engineKind: messageData.engineKind } : {}),
          ...(Array.isArray(messageData.commands) ? { commands: messageData.commands } : {}),
          ...(typeof messageData.contractVersion === 'number' ? { contractVersion: messageData.contractVersion } : {}),
          // MQTT presence specific fields
          clientId: clientId,
          app: messageData.app || '1.0.0',
          ts: messageData.ts && typeof messageData.ts === 'number' && messageData.ts > 0 ? messageData.ts : Math.floor(Date.now() / 1000),
          teamInboxOpen: messageData.teamInboxOpen === true,
          ...(parseWaPairing(messageData.waPairing) ? { waPairing: parseWaPairing(messageData.waPairing) } : {}),
        };

        // Use proper Redux action to upsert client
        dispatch(upsertClient(connectedSocket));
        this.log(`[MQTT] Dispatched upsertClient for ${clientId}`);
      }

      // Get updated state for logging
      const updatedState = store.getState();
      const updatedClients = updatedState?.ConnectedEnginesReducer?.connectedSockets || [];
      
      this.log(`[MQTT] Clients after update:`, updatedClients.map((c: { id: string }) => c.id));
      this.log(`[MQTT] Updated presence: ${updatedClients.length} connected clients`);
      this.log(`[MQTT] ========================================`);

    } catch (error) {
      console.error('[MQTT] Error handling presence message:', error);
    }
  }
} 